import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { 
  Bell, Check, CheckCheck, Trash2, AlertCircle, Info, AlertTriangle,
  FileText, Truck, DollarSign, Settings, Clock, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { buildAuthenticatedWsUrl } from '../utils/wsTicket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationCenter = ({ userId, onNotificationClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      fetchNotifications();
      // Fallback polling every 60s (WS handles real-time push via new_notification)
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  // WebSocket listener for real-time notification push
  useEffect(() => {
    if (!userId) return;

    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let stopped = false;

    const connect = async () => {
      if (stopped) return;
      try {
        // SEC-003 — Auth WS via ticket court-vie (cf. utils/wsTicket.js)
        const url = await buildAuthenticatedWsUrl('/ws/admin/tracking');
        if (!url || stopped) return;
        ws = new WebSocket(url);
        ws.onopen = () => {
          // Keepalive ping toutes les 25s pour éviter le timeout de l'ingress K8s (60s idle)
          pingTimer = setInterval(() => {
            try { ws?.readyState === 1 && ws.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ }
          }, 25000);
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_notification' && data.data) {
              // Add to notifications list instantly
              setNotifications(prev => {
                if (prev.some(n => n.id === data.data.id)) return prev;
                return [data.data, ...prev];
              });
              setUnreadCount(prev => prev + 1);
            }
          } catch { /* ignore */ }
        };
        ws.onclose = () => {
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          if (!stopped) reconnectTimer = setTimeout(connect, 10000);
        };
        ws.onerror = () => ws?.close();
      } catch { /* ignore */ }
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (ws) { ws.onclose = null; ws.close(); }
    };
  }, [userId]);

  const authHeaders = () => {
    return { Authorization: 'Bearer cookie-auth' };
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=50`, { headers: authHeaders() });
      setNotifications(response.data);
      
      // Count unread
      const unread = response.data.filter(n => !n.isRead).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { headers: authHeaders() });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, { headers: authHeaders() });
      toast({
        title: "Toutes les notifications marquees comme lues"
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast({
        title: "Erreur",
        description: "Impossible de marquer les notifications comme lues",
        variant: "destructive"
      });
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { headers: authHeaders() });
      toast({
        title: "Notification supprimee"
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      const errorMsg = error.response?.data?.detail || "Impossible de supprimer la notification";
      toast({
        title: "Erreur",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const clearAllRead = async () => {
    try {
      await axios.delete(`${API}/notifications/clear-all`, { headers: authHeaders() });
      toast({ title: "Notifications lues supprimees" });
      fetchNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Close the sheet
    setIsOpen(false);

    // If callback provided (for opening modals), use it
    if (onNotificationClick && notification.actionType) {
      onNotificationClick(notification);
    }
    // Otherwise navigate to action URL
    else if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const getNotificationIcon = (type, category) => {
    // Icon based on category first, then fallback to type
    const categoryIcons = {
      'DEVIS': <FileText className="w-5 h-5 text-purple-500" />,
      'LOGISTIQUE': <Truck className="w-5 h-5 text-emerald-500" />,
      'TERRAIN': <Truck className="w-5 h-5 text-blue-500" />,
      'FINANCE': <DollarSign className="w-5 h-5 text-amber-500" />,
      'SYSTEME': <Settings className="w-5 h-5 text-gray-500" />
    };
    
    if (categoryIcons[category]) {
      return categoryIcons[category];
    }
    
    switch (type) {
      case 'ACTION_REQUISE':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'ALERTE':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'INFO':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getCategoryBadge = (category) => {
    const categoryConfig = {
      'DEVIS': { label: 'Devis', color: 'bg-purple-100 text-purple-700 border-purple-200' },
      'LOGISTIQUE': { label: 'Logistique', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
      'TERRAIN': { label: 'Terrain', color: 'bg-blue-100 text-blue-700 border-blue-200' },
      'FINANCE': { label: 'Finance', color: 'bg-amber-100 text-amber-700 border-amber-200' },
      'SYSTEME': { label: 'Système', color: 'bg-gray-100 text-gray-700 border-gray-200' }
    };
    
    const config = categoryConfig[category] || categoryConfig['SYSTEME'];
    return (
      <Badge className={`${config.color} border text-[10px] font-medium px-2 py-0.5`}>
        {config.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      'ACTION_REQUISE': { label: 'Action requise', color: 'bg-red-500 text-white' },
      'ALERTE': { label: 'Alerte', color: 'bg-orange-500 text-white' },
      'INFO': { label: 'Info', color: 'bg-blue-500 text-white' }
    };
    
    const config = typeConfig[type] || typeConfig['INFO'];
    return (
      <Badge className={`${config.color} text-[10px] font-medium px-2 py-0.5`}>
        {config.label}
      </Badge>
    );
  };

  const sortedNotifications = [...notifications].sort((a, b) => {
    // Unread first
    if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
    // Then by type priority
    const typePriority = { 'ACTION_REQUISE': 0, 'ALERTE': 1, 'INFO': 2 };
    const aPriority = typePriority[a.notificationType] || 3;
    const bPriority = typePriority[b.notificationType] || 3;
    if (aPriority !== bPriority) return aPriority - bPriority;
    // Finally by date (newest first)
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[420px] sm:w-[480px] p-0">
        {/* Header amélioré */}
        <div className="sticky top-0 z-10 bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
                <p className="text-sm text-gray-500">
                  {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Tout est à jour'}
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs text-gray-600 hover:text-red-600"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Tout lire
              </Button>
            )}
            {notifications.length > 0 && notifications.some(n => n.isRead) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllRead}
                className="text-xs text-gray-400 hover:text-red-500"
                data-testid="clear-all-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Vider
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-100px)]">
          <div className="px-4 py-3">
            {sortedNotifications.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Bell className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-500 font-medium">Aucune notification</p>
                <p className="text-sm text-gray-400 mt-1">Vous êtes à jour !</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedNotifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    data-testid={`notification-item-${notification.id}`}
                    className={`
                      group relative p-4 rounded-xl cursor-pointer transition-all duration-200
                      ${notification.isRead 
                        ? 'bg-gray-50 hover:bg-gray-100' 
                        : notification.notificationType === 'ACTION_REQUISE'
                          ? 'bg-red-50 hover:bg-red-100 border-l-4 border-red-500 shadow-sm'
                          : notification.notificationType === 'ALERTE'
                            ? 'bg-amber-50 hover:bg-amber-100 border-l-4 border-amber-500 shadow-sm'
                            : 'bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-500 shadow-sm'
                      }
                    `}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`
                        flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                        ${notification.isRead ? 'bg-gray-200' : 'bg-white shadow-sm'}
                      `}>
                        {getNotificationIcon(notification.notificationType, notification.category)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Badges row */}
                        <div className="flex items-center gap-2 mb-1.5">
                          {getCategoryBadge(notification.category)}
                          {!notification.isRead && getTypeBadge(notification.notificationType)}
                        </div>
                        
                        {/* Message */}
                        <p className={`text-sm leading-relaxed ${
                          notification.isRead 
                            ? 'text-gray-600' 
                            : 'text-gray-900 font-medium'
                        }`}>
                          {notification.message}
                        </p>
                        
                        {/* Footer */}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1.5 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(notification.createdAt), {
                              addSuffix: true,
                              locale: fr
                            })}
                          </div>
                          
                          {notification.actionUrl && (
                            <div className="flex items-center gap-1 text-xs text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                              Voir détails
                              <ChevronRight className="w-3 h-3" />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Delete button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => deleteNotification(notification.id, e)}
                      >
                        <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                      </Button>
                    </div>
                    
                    {/* Unread indicator */}
                    {!notification.isRead && (
                      <div className="absolute top-4 right-4 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationCenter;
